import { Box, Input, Tab } from '@hawkins/components';
import { IconCheckmark, IconPencil } from '@hawkins/assets';
import { useState, useEffect, useMemo, useRef } from 'react';
import { PseudoIconButton } from './PseudoIconButton';
import { validateFramingIntentLabelValue } from '../utilities/validation';
import styles from './EditableTab.module.css';

interface EditableTabProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
}

export const EditableTab = ({ title, onTitleChange }: EditableTabProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);

  const validationResult = useMemo(
    () => validateFramingIntentLabelValue(editValue),
    [editValue]
  );
  const validationState = {
    status: (validationResult.isValid ? undefined : 'error') as
      | 'error'
      | undefined,
    message: validationResult.message ?? '',
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use setTimeout to ensure the input is fully rendered before focusing
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    setEditValue(title);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (validationState.status !== 'error' && editValue.trim()) {
      onTitleChange(editValue.trim());
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const editingInput = (
    <Box onClick={handleInputClick} className={styles.tabContainer}>
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        validationStatus={validationState.status}
        validationMessage={validationState.message}
        className={styles.tabInput}
      />
      <PseudoIconButton
        onClick={handleSave}
        ariaLabel="Complete Edit"
        icon={IconCheckmark}
      />
    </Box>
  );

  const tabLabel = (
    <Box className={styles.tabContainer} ref={containerRef}>
      <span className={styles.tabLabel}>{title}</span>
      <PseudoIconButton
        onClick={handleStartEdit}
        ariaLabel={`Edit ${title}`}
        icon={IconPencil}
      />
    </Box>
  );

  return (
    <Tab key="framingDecision1">{isEditing ? editingInput : tabLabel}</Tab>
  );
};
